import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Sparkles, Heart } from 'lucide-react';

export const SergiSBrilliantamiRiga = () => {
  return (
    <>
      <Helmet>
        <title>Серьги с бриллиантами в Риге | Бриллиантовые серьги | Pure Atelier</title>
        <meta name="description" content="Серьги с бриллиантами в Риге. Пусеты, серьги-гвоздики с натуральными бриллиантами. Сертификаты GIA/IGI. Лучшие цены. Pure Atelier Riga." />
        <meta name="keywords" content="серьги с бриллиантами Рига, бриллиантовые серьги, пусеты с бриллиантами, diamond earrings Riga" />
        <link rel="canonical" href="https://pureatelier.lv/sergi-s-brilliantami-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/10983780/pexels-photo-10983780.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Серьги с бриллиантами в Риге
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Элегантные серьги с натуральными бриллиантами. Изысканность и качество для особенных моментов.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery?category=earrings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть коллекцию серёг <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересуют%20серьги%20с%20бриллиантами." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Бесплатная консультация
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "Сертифицированные", desc: "Бриллианты GIA и IGI" },
                { icon: Award, title: "Высокое качество", desc: "Ручная работа в Латвии" },
                { icon: Sparkles, title: "Разные стили", desc: "Пусеты, подвесные" },
                { icon: Heart, title: "Идеальный подарок", desc: "В красивой упаковке" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Серьги с бриллиантами от Pure Atelier
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier</strong> предлагает эксклюзивные серьги с бриллиантами в Риге. В нашей коллекции 
                  представлены как классические пусеты (серьги-гвоздики), так и элегантные подвесные серьги.
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Наша коллекция серёг</h3>
                <ul>
                  <li><strong>Бриллиантовые пусеты</strong> — классические серьги-гвоздики с 3 или 4 крапанами</li>
                  <li><strong>Серьги Halo</strong> — с дополнительными бриллиантами вокруг центрального камня</li>
                  <li><strong>Подвесные серьги</strong> — элегантные серьги для особых случаев</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Почему выбирают Pure Atelier?</h3>
                <p>
                  Мы предлагаем только натуральные бриллианты высочайшего качества с международными сертификатами. 
                  Каждая пара серёг тщательно подбирается, чтобы бриллианты были идентичны по цвету и чистоте.
                </p>
                <ul>
                  <li>Сертифицированные натуральные бриллианты</li>
                  <li>Лучшие цены в Латвии</li>
                  <li>Красивая подарочная упаковка</li>
                  <li>Бесплатная консультация</li>
                </ul>
              </div>

              <div className="mt-12 text-center">
                <Link to="/gallery?category=earrings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть все серьги <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Ищете идеальные серьги с бриллиантами?</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Свяжитесь с нами для получения персонального предложения.
            </p>
            <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересуют%20серьги%20с%20бриллиантами." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> Консультация в WhatsApp
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default SergiSBrilliantamiRiga;
